import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FoodTry } from '@/types/baby';

interface BabyStats {
  triedFoodsCount: number;
  totalFoodsCount: number;
  recentTries: FoodTry[];
}

export const useBabiesStatsCache = (babyIds: string[]) => {
  const [statsCache, setStatsCache] = useState<Map<string, BabyStats>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (babyIds.length === 0) {
      setStatsCache(new Map());
      setIsLoading(false);
      return;
    }

    const fetchAllStats = async () => {
      setIsLoading(true);
      
      try {
        // Fetch total food count once
        const { count: totalFoods } = await supabase
          .from('foods')
          .select('*', { count: 'exact', head: true });

        // Fetch all food logs for all babies in one query
        const { data: allFoodLogs, error } = await supabase
          .from('food_logs')
          .select(`
            id,
            baby_id,
            food_id,
            consumed_at,
            preference,
            notes,
            foods (
              name
            )
          `)
          .in('baby_id', babyIds)
          .order('consumed_at', { ascending: false });

        if (error) throw error;

        // Group logs by baby_id
        const logsByBaby = new Map<string, any[]>();
        babyIds.forEach(id => logsByBaby.set(id, []));
        
        allFoodLogs?.forEach(log => {
          if (log.baby_id) {
            const existing = logsByBaby.get(log.baby_id) || [];
            logsByBaby.set(log.baby_id, [...existing, log]);
          }
        });

        // Calculate stats for each baby
        const newCache = new Map<string, BabyStats>();
        
        babyIds.forEach(babyId => {
          const logs = logsByBaby.get(babyId) || [];
          
          // Count unique foods tried
          const uniqueFoods = new Set(logs.map(log => log.food_id));
          const triedFoodsCount = uniqueFoods.size;

          // Get recent tries (last 3)
          const recentTries: FoodTry[] = logs.slice(0, 3).map(log => ({
            food: log.foods?.name || 'Unknown',
            date: log.consumed_at,
            reaction: (log.preference === 'loved' || log.preference === 'liked') ? 'good' : 
                     (log.preference === 'disliked') ? 'concern' : 'mild',
            note: log.notes || undefined
          }));

          newCache.set(babyId, {
            triedFoodsCount,
            totalFoodsCount: totalFoods ?? 0,
            recentTries
          });
        });

        setStatsCache(newCache);
      } catch (error) {
        console.error('Error fetching babies stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllStats();

    // Set up real-time subscription for all babies
    const channel = supabase
      .channel(`all-babies-food-logs-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'food_logs',
          filter: `baby_id=in.(${babyIds.join(',')})`
        },
        () => {
          // Refetch all stats when any food log changes
          fetchAllStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [babyIds.join(',')]);

  const getStatsForBaby = (babyId: string): BabyStats | null => {
    return statsCache.get(babyId) || null;
  };

  return {
    getStatsForBaby,
    isLoading
  };
};
